import type { Choice, Round } from '@pons/contracts';
import { isOpen } from '../round-ui';

export type DemoDetails = {
  dieVotes: number;
  runVotes: number;
  dieOdds: number;
  runOdds: number;
  lockedOdds?: number;
  stake: number;
  pointsFunded?: boolean;
  pointsReturnCents?: number;
};
export type ArenaRound = Round & { demo?: DemoDetails };
export type Odds = { die: number; run: number };
export function demoCoefficient(value: number) {
  if (!Number.isFinite(value) || value < 1 || value > 100) return null;
  const [whole, fraction = ''] = String(value).split('.');
  return `${whole}.${fraction.padEnd(2, '0').slice(0, 2)}`;
}
export const clamp = (n: number, min = 0, max = 1) =>
  Math.max(min, Math.min(max, n));

export function mergeRounds(rounds: ArenaRound[], personal: ArenaRound[]) {
  const map = new Map(rounds.map((r) => [r.id, r]));
  for (const r of personal) {
    const latest = map.get(r.id);
    map.set(
      r.id,
      latest
        ? {
            ...r,
            ...latest,
            choice: r.choice,
            chosenAt: r.chosenAt,
            lockedOdds: r.lockedOdds,
          }
        : r,
    );
  }
  return [...map.values()].filter((r) => r.canonical !== false);
}

export function openQueue(rounds: ArenaRound[], now: number) {
  return rounds
    .filter((r) => !r.choice && isOpen(r, now))
    .sort((a, b) => a.start - b.start || a.id.localeCompare(b.id));
}

export function remainingFraction(
  round: Round,
  now: number,
  prediction = true,
) {
  const end = prediction ? round.predictionClosesAt : round.settlesAt;
  return clamp((end - now) / Math.max(1, end - round.start));
}

export function capChange(r: Round): number | null {
  const current = Number(r.currentMCUsd);
  const initial = Number(r.capUsd);
  return r.currentMCUsd != null &&
    r.capUsd != null &&
    Number.isFinite(current) &&
    Number.isFinite(initial) &&
    initial > 0
    ? (current / initial - 1) * 100
    : null;
}

export function formatCap(value: string | number | null | undefined) {
  if (value == null || !Number.isFinite(Number(value))) return '—';
  const n = Number(value);
  return (
    '$' +
    new Intl.NumberFormat(
      'en-US',
      n >= 1000
        ? { notation: 'compact', maximumFractionDigits: 1 }
        : { maximumFractionDigits: n < 1 ? 4 : 2 },
    ).format(n)
  );
}

export function oddsFor(r: ArenaRound): Odds | null {
  return r.demo
    ? { die: r.demo.dieOdds, run: r.demo.runOdds }
    : r.odds
      ? {
          die: Number(r.odds.coefficients.DIE),
          run: Number(r.odds.coefficients.RUN),
        }
      : null;
}
// Inverse weights: the larger multiplier always receives less space.
// CSS supplies the content-aware minimum; the text never scales down.
export function dieButtonShare(odds: Odds | null) {
  if (
    !odds ||
    !Number.isFinite(odds.die + odds.run) ||
    odds.die <= 0 ||
    odds.run <= 0
  )
    return 0.5;
  return odds.run / (odds.die + odds.run);
}

export type VoteSummary = {
  die: number | null;
  run: number | null;
  diePercent: number | null;
  runPercent: number | null;
};
export function voteSummary(r: ArenaRound): VoteSummary {
  if (r.demo) {
    const { dieVotes: die, runVotes: run } = r.demo;
    const total = die + run;
    const runPercent = total ? Math.round((run / total) * 100) : 0;
    return { die, run, runPercent, diePercent: total ? 100 - runPercent : 0 };
  }
  if (r.votes === 0) return { die: 0, run: 0, diePercent: 0, runPercent: 0 };
  if (r.split == null || r.votes == null)
    return { die: null, run: null, diePercent: null, runPercent: null };
  const runPercent = clamp(r.split, 0, 100);
  // The legacy API supplies a rounded percentage. Recover counts only when
  // exactly one integer is possible; never invent counts for a large sample.
  const low = Math.max(0, Math.ceil(((runPercent - 0.5) * r.votes) / 100));
  const high = Math.min(
    r.votes,
    Math.ceil(((runPercent + 0.5) * r.votes) / 100) - 1,
  );
  const run = low === high ? low : null;
  return {
    die: run == null ? null : r.votes - run,
    run,
    runPercent,
    diePercent: 100 - runPercent,
  };
}

export function demoPrediction(
  r: ArenaRound,
  choice: Choice,
  now: number,
): ArenaRound {
  if (!r.demo || r.choice || !isOpen(r, now)) return r;
  return {
    ...r,
    choice,
    chosenAt: now,
    demo: {
      ...r.demo,
      runVotes: r.demo.runVotes + (choice === 'RUN' ? 1 : 0),
      dieVotes: r.demo.dieVotes + (choice === 'DIE' ? 1 : 0),
      lockedOdds: choice === 'RUN' ? r.demo.runOdds : r.demo.dieOdds,
    },
  };
}
